import React, { useState } from 'react'

import { FormProvider, useForm, Controller } from 'react-hook-form'

import { Typography, Stack, Link, IconButton, Collapse, Divider, useTheme } from '@mui/material'
import { EyeIcon, EyeOffIcon, InfoIcon } from 'lucide-react'

import { Input, Button } from '@repo/ui'
import { SettingsSection, Loader } from 'components'
import { useUserProfileQuery } from 'hooks'
import { useChangePasswordMutation } from 'hooks/api/useChangePasswordMutation'
import { useChangeEmailMutation } from 'hooks/api/useChangeEmailMutation'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

import { TwoFactorSection } from './components/TwoFactorSection'
import { DevicesSection } from './components/DevicesSection'

type EmailChangeFormData = {
  newEmail: string
}

const DEFAULT_EMAIL_CHANGE_VALUES: EmailChangeFormData = {
  newEmail: '',
}

type PasswordChangeFormData = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const DEFAULT_PASSWORD_CHANGE_VALUES: PasswordChangeFormData = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status?: unknown }).status === 'number'
  ) {
    return (error as { status: number }).status
  }
  return undefined
}

/**
 * VRG-490 decision D6/D7 — "Settings → Security" (a sibling of Profile),
 * holding the password-change card (MOVED here from ProfilePage, D7 — see
 * that file for the removal) and the new two-factor authentication surface.
 *
 * D8's app counterpart is a SECTION on the existing Profile page rather than
 * its own route — the platform gets a full page because Settings already
 * has a page per personal concern (Profile, Appearance, Notifications,
 * Controls); the app deliberately stays shallower.
 */
export const SecurityPage = () => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const { data: profile, isLoading: isLoadingProfile } = useUserProfileQuery()

  const [changingEmail, setChangingEmail] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  const { changeEmailAsync, isChangingEmail } = useChangeEmailMutation()
  const { changePasswordAsync, isChangingPassword } = useChangePasswordMutation()

  const emailFormMethods = useForm<EmailChangeFormData>({
    mode: 'onChange',
    defaultValues: DEFAULT_EMAIL_CHANGE_VALUES,
  })
  const {
    handleSubmit: handleEmailSubmit,
    formState: { isDirty: isEmailDirty, errors: emailErrors },
    reset: resetEmailForm,
  } = emailFormMethods

  const passwordFormMethods = useForm<PasswordChangeFormData>({
    mode: 'onChange',
    defaultValues: DEFAULT_PASSWORD_CHANGE_VALUES,
  })
  const {
    handleSubmit: handlePasswordSubmit,
    formState: { isDirty: isPasswordDirty, errors: passwordErrors },
    reset: resetPasswordForm,
  } = passwordFormMethods

  const handleOpenEmailChange = () => {
    // Figma prefills the input with the current email so the user edits it
    // (VRG-490 — moved here from ProfilePage, D7's "Security is how you
    // prove it's you" applied to the login email itself, not just password).
    resetEmailForm({ newEmail: profile?.email || '' })
    setChangingEmail(true)
  }

  const handleCancelEmailChange = () => {
    setChangingEmail(false)
    resetEmailForm(DEFAULT_EMAIL_CHANGE_VALUES)
  }

  const onSubmitEmailChange = async (formData: EmailChangeFormData) => {
    try {
      await changeEmailAsync({
        newEmail: formData.newEmail,
      })

      notification({
        type: 'success',
        title: 'Verification email sent',
        text: 'Check your new email to complete the update.',
      })
      setChangingEmail(false)
      resetEmailForm(DEFAULT_EMAIL_CHANGE_VALUES)
    } catch (error) {
      const status = getErrorStatus(error)
      let errorText = getErrorMessage(error)

      if (status === 401) {
        errorText = 'Current password is incorrect. Please try again.'
      } else if (status === 409) {
        errorText = 'That email address is already in use. Please try a different email.'
      }

      notification({
        type: 'error',
        title: 'Email update failed',
        text: errorText,
      })
    }
  }

  const handleOpenPasswordChange = () => {
    resetPasswordForm(DEFAULT_PASSWORD_CHANGE_VALUES)
    setChangingPassword(true)
  }

  const handleCancelPasswordChange = () => {
    setChangingPassword(false)
    resetPasswordForm(DEFAULT_PASSWORD_CHANGE_VALUES)
  }

  const onSubmitPasswordChange = async (formData: PasswordChangeFormData) => {
    try {
      await changePasswordAsync({
        currentPassword: formData.currentPassword?.trim() || undefined,
        newPassword: formData.newPassword,
        // Figma shows Current + New only; confirmation mirrors the new password.
        confirmPassword: formData.newPassword,
      })

      notification({
        type: 'success',
        title: 'Password updated',
        text: 'Your password has been changed successfully.',
      })
      setChangingPassword(false)
      resetPasswordForm(DEFAULT_PASSWORD_CHANGE_VALUES)
    } catch (error) {
      const status = getErrorStatus(error)
      let errorText = getErrorMessage(error)

      if (status === 401) {
        errorText = 'Current password is incorrect. Please try again.'
      } else if (status === 409) {
        errorText = 'Unable to update password due to a conflict. Please try again.'
      }

      notification({
        type: 'error',
        title: 'Password update failed',
        text: errorText,
      })
    }
  }

  const handleResetPassword = () => {
    // Vera sign-in is OTP/SSO-first (see VERA-657). A forgotten password is
    // recovered by signing in with a one-time code, not an in-app reset.
    notification({
      type: 'info',
      title: 'Reset via sign-in',
      text: 'Sign out and use “Sign in with a one-time code” to regain access, then set a new password here.',
    })
  }

  if (isLoadingProfile) {
    return <Loader />
  }

  // D1 — mandatory enrollment is `platform_admin` ONLY. `platform_manager`,
  // company roles, and every apps/app owner stay opt-in (no subject to bind
  // to on the owner side at all — see the task file's "consequence of #3").
  const isEnrollmentRequired = profile?.role?.name === 'platform_admin'

  return (
    <Stack spacing={8}>
      <SettingsSection title="Login">
        <Stack spacing={4}>
          {/* VRG-490 — moved from Profile (D7's own reasoning, applied to the
              login email too: whoever controls the email that receives
              sign-in codes and password resets controls the account, so it
              belongs beside Password, not on the "who you are" page). Moved,
              not duplicated — ProfilePage's "Login" section is gone. */}
          <Collapse in={changingEmail} unmountOnExit>
            <FormProvider {...emailFormMethods}>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Typography variant="body2">
                  Change email
                </Typography>
                <Controller
                  name="newEmail"
                  control={emailFormMethods.control}
                  rules={{
                    required: 'Email is required',
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: 'Enter a valid email address',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Stack spacing={1}>
                      <Input
                        type="email"
                        placeholder="name@vera.inc"
                        value={field.value}
                        onValueChange={value => field.onChange(value)}
                        error={Boolean(fieldState.error)}
                        disabled={isChangingEmail}
                      />
                      {fieldState.error && (
                        <Typography variant="body4" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
                <Stack direction="row" spacing={2}>
                  <Button
                    type="button"
                    variant="primary"
                    loading={isChangingEmail}
                    disabled={!isEmailDirty || isChangingEmail || Object.keys(emailErrors).length > 0}
                    onClick={handleEmailSubmit(onSubmitEmailChange)}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isChangingEmail}
                    onClick={handleCancelEmailChange}
                  >
                    Close
                  </Button>
                </Stack>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    mt: 1,
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: theme.palette.primary[50],
                  }}
                >
                  <InfoIcon size={18} strokeWidth={1.5} color={theme.palette.text.secondary} />
                  <Typography variant="body4" color="text.secondary">
                    We will send a confirmation email once you&apos;ve saved changes. To update the email, you will have to confirm your new email address.
                  </Typography>
                </Stack>
              </Stack>
            </FormProvider>
          </Collapse>
          <Collapse in={!changingEmail} unmountOnExit>
            <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center">
              <Stack spacing={1}>
                <Typography variant="body2">
                  Email
                </Typography>
                <Typography variant="body3" color="text.secondary">
                  {profile?.email}
                </Typography>
              </Stack>
              <Button
                disableElevation
                type="button"
                onClick={handleOpenEmailChange}
                variant="tertiary">
                Change
              </Button>
            </Stack>
          </Collapse>
          <Divider />
          <Collapse in={changingPassword} unmountOnExit>
            <FormProvider {...passwordFormMethods}>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Typography variant="body2">Change password</Typography>
                <Controller
                  name="currentPassword"
                  control={passwordFormMethods.control}
                  render={({ field, fieldState }) => (
                    <Stack spacing={1}>
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        placeholder="Current password"
                        value={field.value}
                        onValueChange={value => field.onChange(value)}
                        error={Boolean(fieldState.error)}
                        disabled={isChangingPassword}
                        endAdornment={
                          <IconButton
                            size="small"
                            edge="end"
                            aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                            onClick={() => setShowCurrentPassword(v => !v)}
                          >
                            {showCurrentPassword
                              ? <EyeOffIcon size={18} strokeWidth={1.5} />
                              : <EyeIcon size={18} strokeWidth={1.5} />}
                          </IconButton>
                        }
                      />
                      {fieldState.error && (
                        <Typography variant="body4" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
                <Controller
                  name="newPassword"
                  control={passwordFormMethods.control}
                  rules={{
                    required: 'New password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Stack spacing={1}>
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="New password"
                        value={field.value}
                        onValueChange={value => field.onChange(value)}
                        error={Boolean(fieldState.error)}
                        disabled={isChangingPassword}
                        endAdornment={
                          <IconButton
                            size="small"
                            edge="end"
                            aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                            onClick={() => setShowNewPassword(v => !v)}
                          >
                            {showNewPassword
                              ? <EyeOffIcon size={18} strokeWidth={1.5} />
                              : <EyeIcon size={18} strokeWidth={1.5} />}
                          </IconButton>
                        }
                      />
                      {fieldState.error && (
                        <Typography variant="body4" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </Stack>
                  )}
                />
                <Stack direction="row" spacing={3}>
                  <Button
                    type="button"
                    variant="primary"
                    loading={isChangingPassword}
                    disabled={!isPasswordDirty || isChangingPassword || Object.keys(passwordErrors).length > 0}
                    onClick={handlePasswordSubmit(onSubmitPasswordChange)}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isChangingPassword}
                    onClick={handleCancelPasswordChange}
                  >
                    Close
                  </Button>
                </Stack>
                <Typography variant="body4" color="text.secondary">
                  Can&apos;t remember your current password?{' '}
                  <Link
                    component="button"
                    type="button"
                    underline="always"
                    color="text.primary"
                    onClick={handleResetPassword}
                    sx={{ fontWeight: 600 }}
                  >
                    Reset password
                  </Link>
                </Typography>
              </Stack>
            </FormProvider>
          </Collapse>
          <Collapse in={!changingPassword} unmountOnExit>
            <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="center">
              <Stack spacing={1}>
                <Typography variant="body2">Password</Typography>
                <Typography variant="body3" color="text.secondary">
                  ••••••••
                </Typography>
              </Stack>
              <Button
                disableElevation
                type="button"
                onClick={handleOpenPasswordChange}
                variant="tertiary">
                Change
              </Button>
            </Stack>
          </Collapse>
        </Stack>
      </SettingsSection>

      <SettingsSection
        title="Two-factor authentication"
        description="Add a second step when you sign in, using an authenticator app or a text message."
      >
        <TwoFactorSection isEnrollmentRequired={isEnrollmentRequired} />
      </SettingsSection>

      {/* VRG-611 — below Two-factor deliberately: 2FA governs HOW you get in, this
          governs what is currently in. Same concern, one level out. */}
      <SettingsSection
        title="Devices & sessions"
        description="Where you're signed in, and how you signed in recently."
      >
        <DevicesSection />
      </SettingsSection>
    </Stack>
  )
}
